import React, { JSX, useEffect, useReducer, useState } from "react";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { Badge, BadgeStyle } from "components/badges/badge/badge";
import { Button, ButtonSize, ButtonStyle, ButtonType } from "components/buttons/button/button";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { ContextMenu } from "components/context-menu/context-menu/context-menu";
import { ContextMenuAnchor } from "components/context-menu/context-menu-anchor/context-menu-anchor";
import { DataTable } from "components/tables/data-table/data-table";
import { EmptyText } from "components/empty-text/empty-text";
import { FormSelect } from "components/form/form-select/form-select";
import { HeaderBanner, HeaderBannerNavItem } from "components/header-banner/header-banner";
import { HeaderBannerUtils } from "utilities/header-banner-utils";
import { Icons } from "components/icons/constants/icons";
import { Pager } from "components/pager/pager";
import { Paragraph, ParagraphStyle } from "components/typography/paragraph/paragraph";
import { RoleType } from "models/enumerations/users/role-type";
import { RouteUtils } from "utilities/route-utils";
import { ScrollUtils } from "utilities/scroll-utils";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { SelectOption } from "components/form/inputs/select/select";
import { SideContentLeftLayout } from "components/layouts/side-content-left-layout/side-content-left-layout";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { defaultProviderSearchCriteria, useProviderSearch } from "utilities/contexts/search/provider/provider-search-context";
import { sitemap } from "../../../../../sitemap";
import { t } from "utilities/localization/t";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useProviders } from "utilities/hooks/models/providers/use-providers";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { CollectionUtils } from "utilities/collection-utils";
import { Anchor } from "../../../../../components/typography/anchors/anchor/anchor";
import "./user-providers-list-page.scss";
import { set } from "immutable";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UserProvidersListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "user-providers-list-page";
const ITEMS_PER_PAGE = 20;
const DEBOUNCE_TIME = 750;

// #endregion Constants

const STATUS_BOOLEAN_MAP: Record<string, boolean | undefined> = {
    active: true,
    inactive: false,
    "": undefined,
};

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const UserProvidersListPage: React.FC<UserProvidersListPageProps> = validatePageAccess(
    AccessControlKeys.UserProvidersListPage
)((): JSX.Element => {
    const navItems: HeaderBannerNavItem[] = HeaderBannerUtils.getUserManagementNavItems();
    const { initialCriteria: criteria, setCriteria } = useProviderSearch();
    const { record: globalState } = useGlobalState();
    const [searchText, setSearchText] = useState(criteria?.searchText ?? "");

    const isNFPAAdmin = globalState?.currentIdentity?.role?.roleType === RoleType.NfpaAdministrator;

    const statusOptions: SelectOption[] = [
        { text: t("active"), value: "active" },
        { text: t("inactive"), value: "inactive" },
    ];

    const {
        providers,
        rowCount: providerCount,
        isLoading,
    } = useProviders({
        searchText: criteria?.searchText ?? "",
        isActive: STATUS_BOOLEAN_MAP[criteria?.status ?? ""],
        skip: (criteria?.currentPage - 1) * ITEMS_PER_PAGE,
        take: ITEMS_PER_PAGE,
    });

    const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        setCriteria((prev) => ({ ...prev, status: event.target.value, currentPage: 1 }));
    };

    const handleSearchTextInputChange = (search: string) => {
        setSearchText(search);
    };

    const handleSearchTriggered = (debouncedSearchText: string): void => {
        setSearchText(debouncedSearchText);
        setCriteria((prev) => ({ ...prev, searchText: debouncedSearchText, currentPage: 1 }));
    };

    const onClearFilterClick = () => {
        setSearchText("");
        setCriteria(defaultProviderSearchCriteria);
    };

    const onPageClick = (pageNumber: number) => {
        setCriteria((prev) => ({ ...prev, currentPage: pageNumber }));
    };

    useEffect(() => {
        ScrollUtils.scrollToElementBySelector(`.content`);
    }, [providers]);

    const getViewProviderPath = (providerId: number) => {
        return RouteUtils.replacePathParams(sitemap.admin.userManagement.enProviders.info, {
            id: providerId,
        });
    };

    return (
        <div className={CSS_CLASS_NAME}>
            <HeaderBanner navItems={navItems} title={t("userManagement")}>
                {isNFPAAdmin && (
                    <ButtonIcon
                        text={t("addNewENProvider")}
                        buttonSize={ButtonSize.Medium}
                        buttonStyle={ButtonStyle.TertiaryAlt}
                        buttonType={ButtonType.Link}
                        iconType={Icons.Plus}
                        linkPath={sitemap.admin.userManagement.enProviders.new}
                    />
                )}
            </HeaderBanner>
            <SideContentLeftLayout
                sidebarElement={
                    <div className={`${CSS_CLASS_NAME}__sidebar`}>
                        <Paragraph style={ParagraphStyle.Label}>{t("filterResults")}</Paragraph>
                        <div className={`${CSS_CLASS_NAME}__sidebar__search`}>
                            <SearchTextInput
                                debounce={DEBOUNCE_TIME}
                                disabled={isLoading}
                                onSearchTextInputChange={handleSearchTextInputChange}
                                onSearchTriggered={handleSearchTriggered}
                                id={"providerSearch"}
                                placeholder={t("searchByName")}
                                searchTextInputValue={searchText ?? ""}
                            />
                        </div>
                        <FormSelect
                            ariaLabelledBy={t("filterByStatus")}
                            formFieldName={t("filterByStatus")}
                            id="userFilterByStatus"
                            label={t("filterByStatus")}
                            onChange={handleStatusChange}
                            options={statusOptions}
                            placeholder={t("filterByStatus")}
                            labelScreenReaderOnly={true}
                            value={criteria?.status ?? ""}
                        />
                        <Button
                            onClick={onClearFilterClick}
                            size={ButtonSize.Small}
                            style={ButtonStyle.Quaternary}
                            text={t("clearAllFilters")}
                        />
                    </div>
                }>
                <div className="content-wrap">
                    <div className="content">
                        <SkipNavContent>
                            {CollectionUtils.hasValues(providers) ? (
                                <DataTable>
                                    <thead>
                                        <tr>
                                            <th className="name">{t("name")}</th>
                                            <th className="lms-id">{t("lmsID")}</th>
                                            <th className="status">{t("status")}</th>
                                            <th className="action">
                                                <span className="sr-only">{t("action")}</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {providers.map((provider) => (
                                            <tr key={`provider-row-${provider.id}`}>
                                                <td className="name">
                                                    <Anchor
                                                        path={getViewProviderPath(provider.id ?? 0)}
                                                        text={provider.name}
                                                    />
                                                </td>
                                                <td className="lms-id">{provider.id}</td>
                                                <td className="status">
                                                    <Badge
                                                        style={
                                                            provider.isActive
                                                                ? BadgeStyle.Success
                                                                : BadgeStyle.Warning
                                                        }
                                                        text={
                                                            provider.isActive
                                                                ? t("active")
                                                                : t("inactive")
                                                        }
                                                    />
                                                </td>
                                                <td className="action">
                                                    <ContextMenu>
                                                        <ContextMenuAnchor
                                                            hrefPath={getViewProviderPath(
                                                                provider.id ?? 0
                                                            )}
                                                            displayName={t("viewENProvider")}
                                                        />
                                                    </ContextMenu>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DataTable>
                            ) : (
                                <EmptyText table>{t("searchOrFilterToSeeResults")}</EmptyText>
                            )}
                        </SkipNavContent>
                    </div>
                </div>
                {providers != null && providers.length > 0 && (
                    <div className="footer">
                        <Pager
                            currentPage={criteria.currentPage}
                            totalPages={providerCount / ITEMS_PER_PAGE}
                            onPageClick={onPageClick}
                            itemsPerPage={ITEMS_PER_PAGE}
                            totalItems={providerCount}
                        />
                    </div>
                )}
            </SideContentLeftLayout>
        </div>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { UserProvidersListPage };

// #endregion Exports
